import React, { useEffect, useRef, useState, useCallback } from 'react';
import './PWAInstallPrompt.css';

// "Later" should not hide the banner forever. Use a cooldown so it shows again automatically.
const LOCAL_STORAGE_KEY = 'ulsaapp-pwa-dismissed-at';
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBUG_KEY = 'ulsaapp-pwa-debug'; // set to "true" in localStorage to enable logs
const TEST_MODE_KEY = 'ulsaapp-pwa-test-mode'; // set to "true" to ignore cooldown locally

const getDismissedAt = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);

    // Backward compatibility: older versions stored "true"
    if (raw === 'true') {
      return Date.now();
    }

    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isEligible, setIsEligible] = useState(false);
  const cooldownTimerRef = useRef(null);

  const debugLog = useCallback((...args) => {
    try {
      if (localStorage.getItem(DEBUG_KEY) === 'true') {
        // eslint-disable-next-line no-console
        console.log('[PWAInstallPrompt]', ...args);
      }
    } catch {
      // ignore
    }
  }, []);

  const isTestMode = useCallback(() => {
    try {
      const isLocal =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '[::1]';
      if (!isLocal) return false;
      return localStorage.getItem(TEST_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  }, []);

  const handleBeforeInstallPrompt = useCallback((event) => {
    event.preventDefault();
    setDeferredPrompt(event);

    // Debug helpers for local testing
    try {
      if (localStorage.getItem(DEBUG_KEY) === 'true') {
        window.__ULSAAPP_DEFERRED_PROMPT__ = event;
        window.__ULSAAPP_TRIGGER_INSTALL_PROMPT__ = async () => {
          if (!window.__ULSAAPP_DEFERRED_PROMPT__) return { ok: false, reason: 'no deferred prompt' };
          window.__ULSAAPP_DEFERRED_PROMPT__.prompt();
          return window.__ULSAAPP_DEFERRED_PROMPT__.userChoice;
        };
      }
    } catch {
      // ignore
    }

    // If user clicked "Later" recently, don't show the banner yet.
    const dismissedAt = getDismissedAt();
    const age = dismissedAt ? (Date.now() - dismissedAt) : 0;
    const dismissedRecently = dismissedAt && age < DISMISS_COOLDOWN_MS;

    debugLog('beforeinstallprompt fired', {
      dismissedAt,
      dismissedRecently,
      cooldownMs: DISMISS_COOLDOWN_MS,
      hasDeferredPrompt: !!event,
    });

    if (isTestMode() || !dismissedRecently) {
      setIsEligible(true);
      return;
    }

    // IMPORTANT: Chrome fires beforeinstallprompt only once per page load.
    // So if we suppress the banner due to cooldown, we schedule it to re-appear
    // after the remaining cooldown time (without requiring a page refresh).
    const remaining = Math.max(0, DISMISS_COOLDOWN_MS - age);
    if (remaining > 0) {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => {
        debugLog('cooldown expired -> showing banner');
    setIsEligible(true);
      }, remaining);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    const handleAppInstalled = () => {
      setIsEligible(false);
      setDeferredPrompt(null);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, String(Date.now()));
      } catch {
        // ignore
      }
      debugLog('appinstalled fired');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, [handleBeforeInstallPrompt, debugLog]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsEligible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    debugLog('dismiss clicked -> hiding banner');
    setIsEligible(false);
  };

  if (!isEligible || window.matchMedia('(display-mode: standalone)').matches) {
    return null;
  }

  return (
    <div className="pwa-install-banner">
      <button
        type="button"
        className="pwa-install-banner__close"
        onClick={handleDismiss}
        aria-label="Close install banner"
      >
        ✕
      </button>
      <div className="pwa-install-banner__content">
        <h4>Install UlsaApp</h4>
        <p>Get faster access and offline mode by adding UlsaApp to your device.</p>
      </div>
      <div className="pwa-install-banner__actions">
        <button type="button" className="outline" onClick={handleDismiss}>
          Later
        </button>
        <button type="button" onClick={handleInstall}>
          Install
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;

